import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Snackbar,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Medication as MedicationIcon,
  ElectricBolt as SeizureIcon,
  ContentCopy as ContentCopyIcon,
  Scale as ScaleIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import AppStyles from '../styles/AppStyles';
import { Alert } from '@mui/material';
import { CardSkeleton } from '../components/SkeletonLoader';
import ErrorState from '../components/ErrorState';
import { useQuery, useQueryClient } from '@tanstack/react-query';

function Dashboard() {
  const { t } = useTranslation();
  const [error] = useState('');
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';
  const token = typeof window !== 'undefined' ? localStorage.getItem('vetToken') : null;
  const [petId, setPetId] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem('currentPetId') : null));
  
  
  const handleCopyMedications = () => {
    const meds = summary?.medicationLogs || [];
    if (!Array.isArray(meds) || meds.length === 0) {
      navigator.clipboard.writeText('');
      return;
    }
    const lines = meds.map((med) => {
      const name = med?.medicationName || '-';
      const dosage = med?.dosage != null && med?.dosage !== '' ? med.dosage : '-';
      const unit = med?.unit ? ` ${med.unit}` : '';
      const hasFreq = med?.frequencyNumber != null && med?.frequencyNumber !== '';
      const freq = hasFreq ? `${med.frequencyNumber}${med?.frequencyUnit ? ` ${med.frequencyUnit}` : ''}` : t('dashboard.asNeeded');
      // Format: <name> <dosage> <unit>/<frequency>
      return `${name} ${dosage}${unit}/${freq}`.trim();
    });
    navigator.clipboard.writeText(lines.join(', '));
    // If already visible, reset to retrigger animation
    if (copied) {
      setCopied(false);
      setTimeout(() => setCopied(true), 0);
    } else {
      setCopied(true);
    }
  };

  // Patients shared with vet
  const patientsQuery = useQuery({
    queryKey: ['patients'],
    queryFn: async () => {
      const resp = await fetch(`${API_BASE}/api/sharing/me/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || t('dashboard.failedToLoadPatients'));
      return Array.isArray(data?.data) ? data.data : [];
    },
    staleTime: 120000,
  });

  // Ensure a currentPetId exists from patients
  useEffect(() => {
    const pts = patientsQuery.data || [];
    const current = typeof window !== 'undefined' ? localStorage.getItem('currentPetId') : null;
    if ((!current || !pts.find(p => p.petId === current)) && pts.length > 0) {
      localStorage.setItem('currentPetId', pts[0].petId);
      localStorage.setItem('currentPetName', pts[0].petName || '');
      setPetId(pts[0].petId);
    }
  }, [patientsQuery.data]);

  const effectivePetId = useMemo(() => {
    const pts = patientsQuery.data || [];
    if (petId && pts.find(p => p.petId === petId)) return petId;
    return pts[0]?.petId || null;
  }, [patientsQuery.data, petId]);

  // Summary for pet
  const summaryQuery = useQuery({
    queryKey: ['summary', effectivePetId],
    enabled: !!effectivePetId,
    queryFn: async () => {
      const resp = await fetch(`${API_BASE}/api/sharing/me/patients/${effectivePetId}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || t('dashboard.failedToLoadPetSummary'));
      return data?.data || null;
    },
    staleTime: 60000,
  });

  // Seizures for trend
  const seizuresQuery = useQuery({
    queryKey: ['seizures', effectivePetId],
    enabled: !!effectivePetId,
    queryFn: async () => {
      const resp = await fetch(`${API_BASE}/api/sections-seizures?petId=${effectivePetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || t('dashboard.failedToLoadSeizures'));
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60000,
  });

  const summary = summaryQuery.data || null;
  const seizureSeries = useMemo(() => {
    const seizures = seizuresQuery.data || [];
    const byMonth = {};
    seizures.forEach(s => {
      const m = moment(s.date).format('YYYY-MM');
      byMonth[m] = (byMonth[m] || 0) + 1;
    });
    return Object.keys(byMonth).sort().map(m => ({ date: m + '-01', count: byMonth[m] }));
  }, [seizuresQuery.data]);
  const lastSeizureDate = useMemo(() => {
    const seizures = seizuresQuery.data || [];
    const latest = seizures.reduce((acc, s) => {
      const d = new Date(s.date);
      return !acc || d > acc ? d : acc;
    }, null);
    return latest ? latest.toISOString() : null;
  }, [seizuresQuery.data]);

  // Track currentPetId changes to switch query keys immediately
  useEffect(() => {
    const handlePetChange = () => {
      const current = typeof window !== 'undefined' ? localStorage.getItem('currentPetId') : null;
      setPetId(current);
      // Optionally invalidate all active queries to force immediate refetches
      queryClient.invalidateQueries({ predicate: () => true, refetchType: 'active' });
    };
    window.addEventListener('storage', handlePetChange);
    window.addEventListener('petChanged', handlePetChange);
    return () => {
      window.removeEventListener('storage', handlePetChange);
      window.removeEventListener('petChanged', handlePetChange);
    };
  }, [queryClient]);

  const loading = patientsQuery.isLoading || summaryQuery.isLoading || seizuresQuery.isLoading;
  if (loading) {
    return (
      <Box 
        sx={{ 
          background: 'transparent',
          minHeight: '100vh',
          padding: AppStyles.spacing.component.padding,
        }}
      >
        <CardSkeleton count={3} />
      </Box>
    );
  }
  const seizureTotal = (seizureSeries || []).reduce((acc, p) => acc + (p.count || 0), 0);

  if (error || patientsQuery.isError || summaryQuery.isError || seizuresQuery.isError) {
    return <ErrorState />;
  }

  return (
    <Box
      sx={{
        background: 'transparent',
        minHeight: '100vh',
        padding: AppStyles.spacing.component.padding,
      }}
    >
      <Grid container spacing={4}>
        {/* Quick Stats - moved to top */}
        <Grid size={12}>
          <Paper 
            elevation={8}
            sx={{ 
              p: { xs: 2, md: 4 }, 
              borderRadius: 3,
              background: 'transparent',
              border: `1px solid ${AppStyles.colors.border.light}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
              }
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{
                color: AppStyles.colors.primary.dark,
                fontWeight: AppStyles.typography.fontWeight.semibold,
                mb: 3,
              }}
            >
              {t('dashboard.quickOverview')}
            </Typography>
            <Grid container spacing={3}>
              {/* Medications - active count */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    background: `linear-gradient(145deg, ${AppStyles.colors.background.tertiary} 0%, ${AppStyles.colors.background.secondary} 100%)`,
                    borderRadius: AppStyles.borderRadius.lg,
                    border: `1px solid ${AppStyles.colors.border.light}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: AppStyles.shadows.xl,
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 0.75rem',
                      boxShadow: AppStyles.shadows.lg,
                    }}
                  >
                    <MedicationIcon sx={{ color: AppStyles.colors.warning.contrastText, fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      color: AppStyles.colors.warning.dark,
                      fontWeight: AppStyles.typography.fontWeight.bold,
                      mb: 0.5,
                    }}
                  >
                    {(summary?.medicationLogs || []).filter(m => !m.startDate || (m.startDate && !m.endDate) || (m.startDate && m.endDate && new Date(m.endDate) > new Date())).length}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: AppStyles.colors.text.secondary,
                      fontWeight: AppStyles.typography.fontWeight.medium,
                      fontSize: '0.875rem',
                    }}
                  >
                    {(summary?.medicationLogs || []).filter(m => !m.startDate || (m.startDate && !m.endDate) || (m.startDate && m.endDate && new Date(m.endDate) > new Date())).length === 1? t('dashboard.activeMedication') : t('dashboard.activeMedications')}
                  </Typography>
                </Box>
              </Grid>
              {/* Seizures - last 6 months */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    background: `linear-gradient(145deg, ${AppStyles.colors.background.tertiary} 0%, ${AppStyles.colors.background.secondary} 100%)`,
                    borderRadius: AppStyles.borderRadius.lg,
                    border: `1px solid ${AppStyles.colors.border.light}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: AppStyles.shadows.xl,
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.error.main} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 0.75rem',
                      boxShadow: AppStyles.shadows.lg,
                    }}
                  >
                    <SeizureIcon sx={{ color: AppStyles.colors.error.contrastText, fontSize: 24 }} />
                  </Box>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      color: AppStyles.colors.error.dark,
                      fontWeight: AppStyles.typography.fontWeight.bold,
                      mb: 0.5,
                    }}
                  >
                    {seizureSeries.filter(p => moment(p.date).isAfter(moment().subtract(6, 'months'))).reduce((acc, p) => acc + (p.count || 0), 0)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: AppStyles.colors.text.secondary,
                      fontWeight: AppStyles.typography.fontWeight.medium,
                      fontSize: '0.875rem',
                    }}
                  >
                    {seizureSeries.filter(p => moment(p.date).isAfter(moment().subtract(6, 'months'))).reduce((acc, p) => acc + (p.count || 0), 0) === 1 ? t('dashboard.seizure') : t('dashboard.seizures')} {t('dashboard.lastSixMonths')}
                  </Typography>
                </Box>
              </Grid>
              {/* Weight - current value */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    background: `linear-gradient(145deg, ${AppStyles.colors.background.tertiary} 0%, ${AppStyles.colors.background.secondary} 100%)`,
                    borderRadius: AppStyles.borderRadius.lg,
                    border: `1px solid ${AppStyles.colors.border.light}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: AppStyles.shadows.xl,
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.info.main} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 0.75rem',
                      boxShadow: AppStyles.shadows.lg,
                    }}
                  >
                    <ScaleIcon sx={{ color: AppStyles.colors.info.contrastText, fontSize: 24 }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 0.5 }}>
                  <Typography 
                      variant="h4" 
                    sx={{ 
                        color: AppStyles.colors.info.dark,
                      fontWeight: AppStyles.typography.fontWeight.bold,
                      }}
                    >
                      {Array.isArray(summary?.weightLogs) && summary.weightLogs.length > 0 ? summary.weightLogs[summary.weightLogs.length - 1].weightValue : '-'}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: AppStyles.colors.text.secondary,
                        fontWeight: AppStyles.typography.fontWeight.medium,
                        ml: 0.5,
                        fontSize: '0.875rem',
                      }}
                    >
                  </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: AppStyles.colors.text.secondary,
                      fontWeight: AppStyles.typography.fontWeight.medium,
                      fontSize: '0.875rem',
                    }}
                  >
                    {t('dashboard.weightKg')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {/* Current Medication (hide if no data) */}
        {(summary?.medicationLogs || []).length > 0 && (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Paper 
            elevation={8}
            sx={{ 
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              background: 'transparent',
              border: `1px solid ${AppStyles.colors.border.light}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: AppStyles.shadows.md,
                }}
              >
                <MedicationIcon sx={{ color: AppStyles.colors.primary.contrastText }} />
              </Box>
              <Typography 
                variant="h5"
                sx={{
                  color: AppStyles.colors.primary.dark,
                  fontWeight: AppStyles.typography.fontWeight.semibold,
                }}
              >
                {t('dashboard.currentMedication')}
              </Typography>
            </Box>
            <TableContainer sx={{ width: '100%' }}>
              <Table sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ '& th': { color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.medium, fontSize: '0.8rem' } }}>
                    <TableCell>{t('dashboard.medicationName')}</TableCell>
                    <TableCell>{t('dashboard.dosage')}</TableCell>
                    <TableCell>{t('dashboard.frequency')}</TableCell>
                    <TableCell>{t('dashboard.condition')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summary?.medicationLogs || []).filter(m => !m.startDate || (m.startDate && !m.endDate) || (m.startDate && m.endDate && new Date(m.endDate) > new Date())).map((med, index) => (
                    <TableRow 
                      key={index}
                      sx={{
                        '& td': {
                          borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography 
                          variant="body1"
                          sx={{ 
                            color: AppStyles.colors.text.primary,
                            fontWeight: AppStyles.typography.fontWeight.medium,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {med.medicationName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                          {(med.dosage ?? '-')}{med.unit ? ` ${med.unit}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                          {med.frequencyNumber ?? t('dashboard.asNeeded')} {med.frequencyUnit || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ justifySelf: 'start', display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                          <Chip 
                            label={med.condition || '-'}
                            size="small"
                            sx={{
                              borderRadius: 999,
                              px: 1,
                              height: 24,
                              background: `linear-gradient(135deg, ${AppStyles.colors.success.main} 0%, ${AppStyles.colors.success.light} 100%)`,
                              color: AppStyles.colors.success.contrastText,
                              border: `1px solid ${AppStyles.colors.success.light}`,
                              fontWeight: AppStyles.typography.fontWeight.medium,
                              boxShadow: AppStyles.shadows.sm,
                              textTransform: 'capitalize',
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider sx={{ 
              mt: 1, 
              borderColor: AppStyles.colors.border.light,
              opacity: 0.6,
            }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyMedications}
                  sx={{
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: AppStyles.typography.fontWeight.medium,
                    backgroundColor: 'rgba(188, 185, 118, 0.12)',
                  color: AppStyles.colors.primary.dark,
                  borderColor: 'rgba(188, 185, 118, 0.35)',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(188, 185, 118, 0.18)',
                    borderColor: 'rgba(188, 185, 118, 0.45)',
                    boxShadow: 'none',
                  }
                }}
              >
                {t('dashboard.copy')}
              </Button>
            </Box>
          </Paper>
        </Grid>
        )}

        {/* Seizures Trend - below medication (show only if at least 2 entries) */}
        {seizureTotal >= 2 && (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Paper 
            elevation={8}
            sx={{ 
              p: { xs: 2, md: 4 }, 
              minHeight: { xs: 300, md: 350 },
              borderRadius: 3,
              background: 'transparent',
                    border: `1px solid ${AppStyles.colors.border.light}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.error.main} 100%)`,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                  <Box
                    sx={{
                  width: 40,
                  height: 40,
                      borderRadius: '50%',
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.error.main} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                  boxShadow: AppStyles.shadows.md,
                }}
              >
                <SeizureIcon sx={{ color: AppStyles.colors.error.contrastText }} />
              </Box>
              <Typography 
                variant="h5"
                sx={{
                  color: AppStyles.colors.primary.dark,
                  fontWeight: AppStyles.typography.fontWeight.semibold,
                }}
              >
                {t('dashboard.seizuresTrend')}
              </Typography>
            </Box>
            {lastSeizureDate && (
              <Box sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: AppStyles.colors.primary.main,
                }} />
                <Typography sx={{
                  color: AppStyles.colors.primary.dark,
                  fontWeight: AppStyles.typography.fontWeight.medium,
                }}>
                  {t('dashboard.lastSeizure')}
                </Typography>
                <Typography sx={{
                  color: AppStyles.colors.text.primary,
                  fontWeight: AppStyles.typography.fontWeight.semibold,
                }}>
                  {moment(lastSeizureDate).format('MMM DD, YYYY')}
                </Typography>
              </Box>
            )}
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={seizureSeries}
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={AppStyles.colors.border.light} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => {
                      const date = moment(value);
                      const currentYear = moment().year();
                      const year = date.year();
                      if (year < currentYear) {
                        return `${date.format('MMM')} ${year.toString()}`;
                      }
                      return date.format('MMM');
                    }}
                    tick={{ fill: AppStyles.colors.text.secondary }}
                  />
                  <YAxis allowDecimals={false} tick={{ fill: AppStyles.colors.text.secondary }} />
                  <Tooltip 
                    labelFormatter={(value) => moment(value).format('MMM YYYY')}
                    formatter={(value) => [value, t('dashboard.seizures')]}
                    contentStyle={{
                      backgroundColor: AppStyles.colors.background.paper,
                      border: `1px solid ${AppStyles.colors.border.light}`,
                      borderRadius: AppStyles.borderRadius.md,
                      boxShadow: AppStyles.shadows.lg,
                    }}
                  />
                  <Bar dataKey="count" fill="rgba(244, 67, 54, 0.3)" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        )}

        {/* Weight Trend Chart - show only if at least 2 entries */}
        {(summary?.weightLogs || []).length >= 2 && (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Paper 
            elevation={8}
            sx={{ 
              p: { xs: 2, md: 4 }, 
              minHeight: { xs: 400, md: 450 },
              borderRadius: 3,
              background: 'transparent',
              border: `1px solid ${AppStyles.colors.border.light}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.info.main} 100%)`,
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.info.main} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: AppStyles.shadows.md,
                }}
              >
                <TrendingUpIcon sx={{ color: AppStyles.colors.info.contrastText }} />
                  </Box>
                  <Typography 
                variant="h5"
                    sx={{ 
                  color: AppStyles.colors.primary.dark,
                  fontWeight: AppStyles.typography.fontWeight.semibold,
                    }}
                  >
                {t('dashboard.weightTrend')}
                  </Typography>
            </Box>
            {Array.isArray(summary?.weightLogs) && summary.weightLogs.length > 0 && (
              <Box sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: AppStyles.colors.primary.main,
                }} />
                <Typography sx={{
                  color: AppStyles.colors.primary.dark,
                      fontWeight: AppStyles.typography.fontWeight.medium,
                }}>
                  {t('dashboard.currentWeight')}
                </Typography>
                <Typography sx={{
                  color: AppStyles.colors.text.primary,
                  fontWeight: AppStyles.typography.fontWeight.semibold,
                }}>
                  {summary.weightLogs[summary.weightLogs.length - 1].weightValue} kg
                </Typography>
              </Box>
            )}
            <Box sx={{ width: '100%', height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={(summary?.weightLogs || []).map(w => ({ date: w.date, weight: w.weightValue }))} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={AppStyles.colors.border.light} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => {
                      const date = moment(value);
                      const currentYear = moment().year();
                      const year = date.year();
                      if (year < currentYear) {
                        return `${date.format('MMM DD')} ${year.toString()}`;
                      }
                      return date.format('MMM DD');
                    }}
                    tick={{ fill: AppStyles.colors.text.secondary }}
                  />
                  <YAxis tick={{ fill: AppStyles.colors.text.secondary }} />
                  <Tooltip 
                    labelFormatter={(value) => moment(value).format('MMM DD, YYYY')}
                    formatter={(value) => [`${value} kg`, t('dashboard.weight')]}
                    contentStyle={{
                      backgroundColor: AppStyles.colors.background.paper,
                      border: `1px solid ${AppStyles.colors.border.light}`,
                      borderRadius: AppStyles.borderRadius.md,
                      boxShadow: AppStyles.shadows.lg,
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="weight" 
                    stroke={AppStyles.colors.primary.main}
                    strokeWidth={3}
                    dot={{ 
                      fill: AppStyles.colors.primary.main, 
                      strokeWidth: 2, 
                      r: 6,
                      stroke: AppStyles.colors.background.paper,
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
                </Box>
          </Paper>
        </Grid>
        )}

      </Grid>
      <Snackbar
        open={copied}
        onClose={() => setCopied(false)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setCopied(false)} 
          severity="success" 
          variant="filled"
          sx={{
            background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
            color: AppStyles.colors.primary.contrastText,
            boxShadow: AppStyles.shadows.md,
            fontWeight: AppStyles.typography.fontWeight.medium,
          }}
        >
          {t('dashboard.medicationsCopied')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
